import { useMemo, useState } from "react";
import { T, fontDisplay, fontMono } from "../theme";
import { fmtDay, fmtMoney, isoDate, todayStr } from "../engine/dates";

/**
 * Everything the forecast is counting on that nobody has invoiced yet.
 *
 * The grid answers "what does the position look like"; this answers the
 * question underneath it — "what has to actually happen for that to be true".
 * Every line here is work that has been quoted, promised or typed in, and none
 * of it has become an invoice. If it does not close, the forecast does not
 * happen.
 *
 * Retainers are deliberately absent. They are a standing arrangement rather
 * than something to be won, and including them would bury the handful of things
 * that genuinely need chasing under a wall of monthly repeats.
 *
 * Money already invoiced is absent for the same reason: it is a debt now, and
 * chasing it is a different job from closing work.
 */

/** What stands between this line and an invoice. */
function standing(t) {
  const s = t.source || {};
  if (s.type === "manual_entry") return { label: "Added by hand", tone: T.muted, note: s.description || "" };
  if (s.type === "scoro_quote") {
    return {
      label: s.stage || "Quote",
      tone: s.stageColour || T.pipeline,
      note: s.quoteName || s.description || "",
      docNo: s.docNo,
      probability: s.probability,
    };
  }
  return { label: s.type || "—", tone: T.muted, note: s.description || "" };
}

/**
 * The cost of delivering a line, wherever it happens to be recorded.
 *
 * A quote carries its costs as attachments. Income typed in from a pipeline
 * sheet does not — the costs came in as their own entries, tied to it only by
 * the row they both came from. Matching on that is not elegant, but the
 * alternative is showing £25,000 of income with no sign of the £21,250 it takes
 * to earn it, which is the more misleading of the two.
 */
function costOf(line, spendByDoc, allTxns) {
  const src = line.source || {};
  const attached = spendByDoc?.[`${src.type}:${src.id}`] || [];
  if (attached.length) {
    return {
      total: attached.reduce((t, a) => t + Math.abs(Number(a.gross ?? a.amount) || 0), 0),
      how: attached.length === 1 ? attached[0].supplier_name || attached[0].description : `${attached.length} costs`,
      linked: true,
    };
  }
  // Costs that name this piece of work, whatever row of whatever sheet they
  // arrived on. The link is recorded rather than read out of a note.
  const me = `${src.type}:${src.id}`;
  const mates = (allTxns || []).filter((t) => t.amount < 0 && t.source?.linkedDoc === me);
  if (!mates.length) return null;
  return {
    total: mates.reduce((t, m) => t + Math.abs(m.amount), 0),
    how: "not categorised yet",
    linked: false,
  };
}

/**
 * What a line is worth before VAT.
 *
 * A cash forecast deals in gross, because gross is what moves through the
 * bank. Commercially it is the wrong number: VAT is collected on somebody
 * else's behalf and handed to HMRC, so a £30,000 job is £25,000 of work. On a
 * list of what still has to be won, gross overstates the task by a fifth.
 *
 * A quote states its own net and VAT, so that figure is exact. Income typed in
 * by hand states neither, and is netted at the standard rate — right for the
 * pipeline lines it was loaded from, and flagged rather than assumed silently.
 */
function netOf(t) {
  if (typeof t.net === "number" && t.net) {
    return {
      value: Math.abs(t.net),
      exact: t.source?.type !== "manual_entry" || !!t.vatStated,
      rate: t.vatRate,
      // A hand-entered line has no VAT of its own, so the rate is read from
      // what this client is actually invoiced — right almost always, and worth
      // saying so rather than presenting it as stated fact.
      inferred: t.source?.type === "manual_entry" && !t.vatStated,
    };
  }
  return { value: t.amount, exact: true };
}

export default function ToCloseView({ forecast, openingBalance, weeks, weeksWithout, onOpen }) {
  const [by, setBy] = useState("month");
  const [exVat, setExVat] = useState(false);
  const [busy, setBusy] = useState(false);
  const today = todayStr();

  const lines = useMemo(() => {
    return (forecast?.txns || [])
      .filter(
        (t) =>
          t.amount > 0 &&
          !t.isReceived &&
          !t.isHistory &&
          ["scoro_quote", "manual_entry"].includes(t.source?.type)
      )
      .map((t) => ({
        ...t,
        when: isoDate(t.date),
        month: isoDate(t.date).slice(0, 7),
        days: Math.round((t.date - new Date(`${today}T00:00:00Z`)) / 86400000),
        info: standing(t),
        cost: costOf(t, forecast?.spendByDoc, forecast?.txns),
        netInfo: netOf(t),
      }))
      .sort((a, b) => a.when.localeCompare(b.when) || b.amount - a.amount);
  }, [forecast, today]);

  /** What a line is worth under the current view. */
  const worth = (l) => (exVat ? l.netInfo.value : l.amount);
  /** A cost is stated gross too, so it is netted the same way. */
  const costWorth = (l) => (l.cost ? (exVat ? l.cost.total / 1.2 : l.cost.total) : 0);

  const total = lines.reduce((s, l) => s + worth(l), 0);
  const costTotal = lines.reduce((s, l) => s + costWorth(l), 0);
  const anyAssumed = lines.some((l) => !l.netInfo.exact);

  /**
   * The next ninety days only.
   *
   * Far enough out, the forecast is mostly retainers against a full cost base,
   * because work that has not been quoted yet cannot be in it. The line falls
   * away by construction, so a low point taken over the whole horizon measures
   * the absence of data rather than the state of the business — and it lands in
   * a month nobody is deciding anything about today.
   */
  const horizon = useMemo(() => {
    const cutoff = new Date(`${today}T00:00:00Z`);
    cutoff.setUTCDate(cutoff.getUTCDate() + 90);
    return (weeks || []).filter((w) => !w.isHistory && w.start <= cutoff);
  }, [weeks, today]);

  /**
   * The position if none of it closes — the whole forecast rebuilt without it.
   *
   * Not the income lines subtracted, which was the first attempt and was wrong:
   * work that does not happen costs nothing to deliver, so taking the income
   * out while leaving the media spend and the freelancers in describes a
   * version of events nobody is forecasting. The rebuild removes what hangs off
   * each quote as well, and recalculates the card, since spend that never went
   * on it is never repaid either.
   */
  const without = useMemo(() => {
    if (!weeksWithout?.length) return null;
    const cutoff = new Date(`${today}T00:00:00Z`);
    cutoff.setUTCDate(cutoff.getUTCDate() + 90);
    const inWindow = weeksWithout.filter((w) => !w.isHistory && w.start <= cutoff);
    if (!inWindow.length) return null;
    const low = inWindow.reduce((m, w) => (!m || w.endBal < m.endBal ? w : m), null);
    return { bal: low.endBal, end: low.end };
  }, [weeksWithout, today]);

  const lowNow = useMemo(() => {
    if (!horizon.length) return null;
    return horizon.reduce((m, w) => (!m || w.endBal < m.endBal ? w : m), null);
  }, [horizon]);

  /** What is on the list inside that window, as opposed to all of it. */
  const inHorizon = useMemo(() => {
    if (!horizon.length) return lines;
    const last = horizon[horizon.length - 1].end;
    return lines.filter((l) => l.date <= last);
  }, [lines, horizon]);

  const groups = useMemo(() => {
    const out = {};
    for (const l of lines) {
      const k = by === "month" ? l.month : l.label;
      (out[k] = out[k] || []).push(l);
    }
    return Object.entries(out)
      .map(([k, list]) => ({ key: k, list, total: list.reduce((s, l) => s + worth(l), 0) }))
      .sort((a, b) => (by === "month" ? a.key.localeCompare(b.key) : b.total - a.total));
  }, [lines, by, exVat]);

  const monthName = (m) =>
    new Date(`${m}-01T00:00:00Z`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <div>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 22, margin: "0 0 4px" }}>Still to close</h2>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 760, marginTop: 0 }}>
        Everything the forecast counts on that has not been invoiced. Quotes not yet won, and income
        added by hand. Retainers are left out — they are a standing arrangement, not something to
        win — and so is anything already invoiced, because chasing a debt is a different job.
        Where the work carries third-party cost, that is shown against it: what matters is what the
        business keeps, not what it bills. A cost marked <strong>*</strong> was matched from the
        pipeline sheet rather than attached to the document.
      </p>

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", margin: "14px 0" }}>
        <div style={{ background: T.panel, borderRadius: 10, padding: "12px 16px", minWidth: 190 }}>
          <div style={{ fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: T.muted }}>
            Has to close{exVat ? " · excluding VAT" : ""}
          </div>
          <div style={{ fontFamily: fontDisplay, fontSize: 28, color: T.pipeline }}>
            {fmtMoney(total)}
          </div>
          <div style={{ fontSize: 11, color: T.muted }}>{lines.length} things</div>
          {costTotal > 0 && (
            <div style={{ fontSize: 11, color: T.muted, marginTop: 6, lineHeight: 1.5 }}>
              less {fmtMoney(costTotal)} of third-party cost
              <br />
              <strong style={{ color: T.income }}>{fmtMoney(total - costTotal)}</strong> stays in the
              business
            </div>
          )}
        </div>

        {lowNow && without && (
          <div style={{ background: T.panel, borderRadius: 10, padding: "12px 16px", minWidth: 260 }}>
            <div style={{ fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: T.muted }}>
              Lowest point if none of it lands · 90 days
            </div>
            <div
              style={{ fontFamily: fontDisplay, fontSize: 28, color: without.bal < 0 ? T.cost : T.income }}
            >
              {fmtMoney(without.bal)}
            </div>
            <div style={{ fontSize: 11, color: T.muted }}>
              against {fmtMoney(lowNow.endBal)} as forecast · {fmtDay(without.end)}
            </div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
              {fmtMoney(inHorizon.reduce((t, l) => t + worth(l), 0))} of the list falls inside it,
              and the cost of delivering it comes out too
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: T.muted }}>Showing</span>
        {[
          [false, "with VAT — what hits the bank"],
          [true, "without VAT — what the work is worth"],
        ].map(([k, l]) => (
          <button
            key={String(k)}
            onClick={() => setExVat(k)}
            style={{
              padding: "5px 10px",
              fontSize: 11,
              borderRadius: 6,
              border: `1px solid ${exVat === k ? T.accent : T.border}`,
              background: exVat === k ? T.accentSoft : "transparent",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {l}
          </button>
        ))}
        {exVat && anyAssumed && (
          <span style={{ fontSize: 11, color: T.muted }}>
quotes use their own VAT; typed-in lines use the rate that client is actually charged
          </span>
        )}
        <button
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const { exportToClose } = await import("../lib/exportExcel");
              await exportToClose({
                lines,
                asAt: fmtDay(today),
                exposure: without && lowNow ? { without: without.bal, forecast: lowNow.endBal } : null,
              });
            } finally {
              setBusy(false);
            }
          }}
          style={{
            marginLeft: 14,
            padding: "5px 12px",
            fontSize: 11,
            borderRadius: 6,
            border: `1px solid ${T.border}`,
            background: T.panelSoft,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
          title="A workbook of everything still to win, priced with and without VAT"
        >
          {busy ? "Building…" : "Export"}
        </button>

        <span style={{ fontSize: 11, color: T.muted, marginLeft: 14 }}>Group by</span>
        {[
          ["month", "when it lands"],
          ["client", "client"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setBy(k)}
            style={{
              padding: "5px 10px",
              fontSize: 11,
              borderRadius: 6,
              border: `1px solid ${by === k ? T.accent : T.border}`,
              background: by === k ? T.accentSoft : "transparent",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {l}
          </button>
        ))}
      </div>

      {!lines.length && (
        <div style={{ background: T.panel, borderRadius: 10, padding: 20, fontSize: 13, color: T.muted }}>
          Nothing outstanding — everything in the forecast has been invoiced.
        </div>
      )}

      {groups.map((g) => (
        <div key={g.key} style={{ marginBottom: 14 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 10,
              padding: "6px 2px",
              borderBottom: `1px solid ${T.border}`,
              marginBottom: 4,
            }}
          >
            <strong style={{ fontSize: 14 }}>
              {by === "month" ? monthName(g.key) : g.key}
            </strong>
            <span style={{ fontFamily: fontMono, fontSize: 13, color: T.pipeline }}>
              {fmtMoney(g.total)}
            </span>
            <span style={{ fontSize: 11, color: T.muted }}>
              {g.list.length} thing{g.list.length === 1 ? "" : "s"}
            </span>
            {g.list.some((l) => l.cost) && (
              <span style={{ fontSize: 11, color: T.muted, marginLeft: "auto" }}>
                {fmtMoney(g.list.reduce((t, l) => t + costWorth(l), 0))} of cost ·{" "}
                <strong style={{ color: T.income }}>
                  {fmtMoney(g.total - g.list.reduce((t, l) => t + costWorth(l), 0))}
                </strong>{" "}
                kept
              </span>
            )}
          </div>

          {g.list.map((l) => (
            <button
              key={l.key}
              onClick={() => onOpen?.(l)}
              style={{
                display: "flex",
                width: "100%",
                gap: 10,
                alignItems: "center",
                padding: "8px 2px",
                border: "none",
                borderBottom: `1px solid ${T.borderSoft}`,
                background: "transparent",
                cursor: "pointer",
                fontFamily: "inherit",
                textAlign: "left",
              }}
            >
              <span style={{ fontFamily: fontMono, fontSize: 13, color: T.pipeline, minWidth: 92 }}>
                {fmtMoney(worth(l))}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 12 }}>{by === "month" ? l.label : l.info.note || l.label}</span>
                {by === "month" && l.info.note && (
                  <span style={{ fontSize: 11, color: T.muted }}> · {l.info.note.slice(0, 60)}</span>
                )}
                {l.info.docNo && (
                  <span style={{ fontFamily: fontMono, fontSize: 10, color: T.muted }}> #{l.info.docNo}</span>
                )}
              </span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: l.info.tone,
                  whiteSpace: "nowrap",
                }}
              >
                {l.info.label}
                {l.info.probability != null && l.info.probability < 100 ? ` · ${l.info.probability}%` : ""}
              </span>
              {l.cost && (
                <span
                  style={{ fontFamily: fontMono, fontSize: 11, color: T.cost, minWidth: 150, textAlign: "right" }}
                  title={`${l.info.note || l.label} costs ${fmtMoney(l.cost.total)} to deliver${l.cost.linked ? "" : " — matched from the pipeline sheet, not attached"}`}
                >
                  −{fmtMoney(costWorth(l))}
                  <span style={{ color: T.muted, fontSize: 10 }}>
                    {" "}
                    {l.cost.linked ? "cost" : "cost*"}
                  </span>
                </span>
              )}
              <span style={{ fontSize: 11, color: T.muted, minWidth: 96, textAlign: "right" }}>
                {fmtDay(l.when)}
                {l.days >= 0 ? ` · ${l.days}d` : ""}
              </span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
